/**
 * pricingProvider.js — interface boundary for live mortgage pricing.
 *
 * LoanKnow has no live pricing feed yet. The engine is designed to work
 * without one: products whose ranking depends on live pricing (ARMs, IO)
 * are flagged for comparison instead of being declared "better".
 *
 * @typedef {Object} ProductPricing
 * @property {string} termProgramId
 * @property {number=} interestRate
 * @property {number=} apr
 * @property {number=} points
 * @property {number=} principalAndInterest
 * @property {number=} totalMonthlyPayment
 */

/** Live pricing: none available today. Never fabricate rates. */
export const nullPricingProvider = {
  /** @returns {ProductPricing|null} */
  getPricing() {
    return null;
  },
  available: false,
};

/**
 * A single flat screening rate used ONLY to compare required payments across
 * amortization terms for the affordability guardrail. It is applied equally
 * to every product (no fixed-vs-ARM spread is assumed), is clearly not an
 * offered rate, and is never shown to the borrower as pricing.
 */
export const SCREENING_ANNUAL_RATE = 6.5;

/** Standard amortizing monthly P&I for the screening comparison. */
export function screeningMonthlyPI(loanAmount, termYears, annualRate = SCREENING_ANNUAL_RATE) {
  if (!loanAmount || !termYears) return null;
  const r = annualRate / 100 / 12;
  const n = termYears * 12;
  return (loanAmount * r) / (1 - Math.pow(1 + r, -n));
}
